'use strict';

/**
 * HD-1832: Marketing Attribution Cookie Capture
 *
 * Captures UTM parameters and click identifiers from the URL and stores them
 * in browser cookies on every page load. No cookie consent check is applied.
 *
 * Cookie behaviour:
 *   campaign_first_interaction_url  - Set once on first visit, never overwritten
 *   campaign_last_interaction_url   - Always overwritten on every page load (HD-2283)
 *   fbclid / gclid / msclkid       - Always overwritten with the latest value
 *   campaign_first_utm_*            - Set once (first-touch), never overwritten
 *   campaign_last_utm_*             - Always updated with the latest value
 *
 * Guard: If campaign_first_utm_campaign already exists, no other
 *        campaign_first_utm_* cookies are newly set to prevent mismatched
 *        attribution data.
 */

var COOKIE_MAX_AGE_DAYS = 365;

/**
 * Reads a cookie value by name.
 * @param {string} name
 * @returns {string|null}
 */
function getCookie(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) {
        return decodeURIComponent(parts.pop().split(';').shift());
    }
    return null;
}

/**
 * Writes a cookie with a 365-day expiry.
 * @param {string} name
 * @param {string} value
 */
function setCookie(name, value) {
    var date = new Date();
    date.setTime(date.getTime() + (COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000));
    var expires = 'expires=' + date.toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + ';' + expires + ';path=/;SameSite=Strict;Secure';
}

/**
 * Returns a single URL query parameter value.
 * @param {string} name
 * @returns {string|null}
 */
function getUrlParam(name) {
    var search = window.location.search;
    var regex = new RegExp('[?&]' + name + '=([^&#]*)');
    var match = regex.exec(search);
    return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
}

/**
 * Captures all attribution parameters and stores them in cookies.
 */
function captureAttributionCookies() {
    // 1. Entry URL - first-touch only, never overwritten
    if (!getCookie('campaign_first_interaction_url')) {
        setCookie('campaign_first_interaction_url', window.location.href);
    }

    // HD-2283: Last user-facing page - always overwritten on every page load so
    // the backend reads a real page URL instead of the submission request URL
    setCookie('campaign_last_interaction_url', window.location.href);

    // 2. Click identifiers - always overwrite with the latest captured value
    ['fbclid', 'gclid', 'msclkid'].forEach(function (param) {
        var value = getUrlParam(param);
        if (value) {
            setCookie(param, value);
        }
    });

    // 3. UTM parameters - first-touch and last-touch
    var utmParams = ['utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'utm_term'];

    // Guard: if campaign_first_utm_campaign already exists, do not set any other
    // campaign_first_utm_* cookies to avoid mismatched attribution data
    var hasFirstCampaign = !!getCookie('campaign_first_utm_campaign');

    utmParams.forEach(function (param) {
        var value = getUrlParam(param);
        if (!value) return;

        var firstKey = 'campaign_first_' + param;
        var lastKey = 'campaign_last_' + param;
        var existingFirst = getCookie(firstKey);

        if (!existingFirst) {
            // No first-touch value yet
            if (!hasFirstCampaign) {
                // Only write first-touch when campaign_first_utm_campaign is absent
                setCookie(firstKey, value);
            }
            // Always write last-touch on first capture
            setCookie(lastKey, value);
        } else {
            // First-touch already captured: preserve it, update last-touch only
            setCookie(lastKey, value);
        }
    });
}

module.exports = function () {
    captureAttributionCookies();
};
